import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Logo } from './Logo';
const links = [{
  index: '01',
  label: 'Work with me',
  id: 'work'
}, {
  index: '02',
  label: 'Scope',
  id: 'scope'
}, {
  index: '03',
  label: 'Record',
  id: 'record'
}, {
  index: '04',
  label: 'Writing',
  id: 'index'
}, {
  index: '05',
  label: 'Enquiries',
  id: 'contact',
  accent: true
}];
export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const y = window.scrollY;
      setScrolled(y > 12);
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, Math.max(0, y / max)) : 0);
      let current: string | null = null;
      for (const link of links) {
        const el = document.getElementById(link.id);
        if (el && el.getBoundingClientRect().top <= 96) current = link.id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  const scrollTo = (id: string) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  return <header className="sticky top-0 z-50 bg-bone/85 backdrop-blur-[12px]" data-id="element-105" data-mp="30m8mxn220pg7">
      <div className={`relative mx-auto flex max-w-shell items-center gap-4 px-[clamp(20px,5vw,64px)] transition-[height] duration-300 ease-editorial ${scrolled ? 'h-[56px]' : 'h-[68px]'}`} data-id="element-106" data-mp="2vse3mkqd2chn">
        <Logo onClick={() => {
        setOpen(false);
        window.scrollTo({
          top: 0,
          behavior: reduce ? 'auto' : 'smooth'
        });
      }} data-id="element-107" data-mp="k6dbhfrb7i58" />

        <nav aria-label="Primary" className="ml-auto hidden items-center gap-7 md:flex" data-id="element-108" data-mp="a8fp6xt0fvf9">
          {links.map(link => {
          const isActive = active === link.id;
          return <button key={link.id} onClick={() => scrollTo(link.id)} aria-current={isActive ? 'true' : undefined} className="group relative flex items-baseline gap-1.5 py-1.5" data-id="element-109" data-mp="189bpbxbavg3">
                <span className={`font-mono text-[9.5px] tracking-[0.14em] transition-colors duration-200 ease-editorial ${isActive || link.accent ? 'text-signal' : 'text-ink-45 group-hover:text-signal'}`} data-id="element-110" data-mp="1vjo7oagx1r4a">
                  {link.index}
                </span>
                <span className={`text-[13.5px] transition-colors duration-200 ease-editorial ${isActive ? 'text-ink' : 'text-ink-70 group-hover:text-ink'}`} data-id="element-111" data-mp="2ji5dp97a7mwu">
                  {link.label}
                </span>
                <span aria-hidden="true" className={`absolute -bottom-0.5 left-0 h-px w-full origin-left bg-signal transition-transform duration-200 ease-editorial ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-112" data-mp="2off6mytvqgpf" />
              </button>;
        })}
        </nav>

        <button onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? 'Close menu' : 'Open menu'} className="-mr-2 ml-auto flex h-11 w-11 items-center justify-center text-ink transition-colors duration-200 ease-editorial hover:text-signal md:hidden" data-id="element-113" data-mp="141ewkfeyx20x">
          {open ? <X className="h-5 w-5" aria-hidden="true" data-id="element-114" data-mp="1jha9hc9d9290" /> : <Menu className="h-5 w-5" aria-hidden="true" data-id="element-115" data-mp="mqrfhr4cwx7n" />}
        </button>

        {/* hairline + reading progress */}
        <span aria-hidden="true" className={`absolute bottom-0 left-0 h-px w-full transition-colors duration-300 ease-editorial ${scrolled ? 'bg-rule' : 'bg-transparent'}`} data-id="element-116" data-mp="qteecbzg3iec" />
        <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full origin-left bg-signal" style={{
        transform: `scaleX(${progress})`
      }} data-id="element-117" data-mp="10jqza5b69dl0" />
      </div>

      <AnimatePresence data-id="element-118" data-mp="8024oput5k4b">
        {open && <motion.nav id="mobile-nav" aria-label="Primary mobile" initial={reduce ? false : {
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.2,
        ease: [0.2, 0, 0, 1]
      }} className="border-t border-rule bg-bone md:hidden" data-id="element-119" data-mp="i4111zb5390">
            <div className="mx-auto max-w-shell px-[clamp(20px,5vw,64px)]" data-id="element-120" data-mp="3dpe1vz7j6vbs">
              {links.map(link => {
            const isActive = active === link.id;
            return <button key={link.id} onClick={() => scrollTo(link.id)} aria-current={isActive ? 'true' : undefined} className="flex w-full items-baseline gap-3 border-b border-rule-soft py-4 text-left last:border-b-0" data-id="element-121" data-mp="33yt4ug7i9yuz">
                    <span className={`font-mono text-[9.5px] tracking-[0.14em] ${isActive || link.accent ? 'text-signal' : 'text-ink-45'}`} data-id="element-122" data-mp="2q0corynr6w06">
                      {link.index}
                    </span>
                    <span className={`font-mono text-[12px] uppercase tracking-[0.12em] ${isActive ? 'text-ink' : 'text-ink-70'}`} data-id="element-123" data-mp="26eb6po9nxxx0">
                      {link.label}
                    </span>
                  </button>;
          })}
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}